import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  GuardResult,
  MaybeAsync,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthenticationService } from '@flexyoo/auth';
import {
  EmployeePlanInvitationDataAccessService,
  EmployeeSettingsService,
} from '@flexyoo/employee/data-access';
import { UserAgreementService } from '@flexyoo/employee/ui';
import {
  getEmployeeDefaultRoute,
  hasEmployeeMobilityBudgetAccess,
  isDualPlanEmployee,
  isMobilityBudgetInvited,
  isSamePathname,
} from '@flexyoo/employee/util';
import { map, of, switchMap } from 'rxjs';
import { appRoutesPerRoleDefaults } from 'src/app/app-routes-per-role-defaults.constant';
import { RoleEnum } from 'src/app/shared/models/general/general-enum';

@Injectable({ providedIn: 'root' })
export class EmployeeMobilityBudgetGuard implements CanActivate, CanActivateChild {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthenticationService);
  private readonly employeeSettingsService = inject(EmployeeSettingsService);
  private readonly userAgreementService = inject(UserAgreementService);
  private readonly employeePlanInvitationService = inject(EmployeePlanInvitationDataAccessService);

  canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> {
    return this.checkAccess(state);
  }

  canActivateChild(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> {
    return this.checkAccess(state);
  }

  private checkAccess(state: RouterStateSnapshot): MaybeAsync<GuardResult> {
    const user = this.auth.getCurrentUser();
    if (!user) {
      this.auth.logout();
      return of(false);
    }

    if (user.Role === RoleEnum.Employee) {
      return this.employeeSettingsService.updateAndLoadEmployeeSettings(user.Id).pipe(
        switchMap(({ employeeSettings, tenantSettings }) =>
          this.employeePlanInvitationService.loadEmployeePlanInvitation(user.EmployeeId).pipe(
            map((invitation) => {
              const fallbackRoute = getEmployeeDefaultRoute(
                employeeSettings,
                tenantSettings,
                invitation
              );
              const currentUrl = state.url || this.router.url;

              if (isDualPlanEmployee(employeeSettings) && !isMobilityBudgetInvited(invitation)) {
                if (isSamePathname(currentUrl, fallbackRoute)) {
                  // Already on the fallback: allow it. Navigating to the same URL
                  // re-runs this guard and loops GET /employees/{id} (e.g. HR proxy).
                  return true;
                }

                void this.router.navigate([fallbackRoute]);
                return false;
              }

              if (hasEmployeeMobilityBudgetAccess(employeeSettings, tenantSettings)) {
                return true;
              }

              // Only force account when no plan agreement is signed yet.
              // Dual-plan employees with cafeteria signed should stay in the app.
              const userAgreementState = this.userAgreementService.checkUserAgreementState(
                employeeSettings,
                tenantSettings,
                invitation
              );

              const redirectRoute =
                userAgreementState === 'to-be-uploaded' ? '/employee-account' : fallbackRoute;

              if (isSamePathname(currentUrl, redirectRoute)) {
                // Already on the redirect target: allow it. Navigating to the same URL
                // re-runs this guard and loops GET /employees/{id} (e.g. HR proxy).
                return true;
              }

              void this.router.navigate([redirectRoute]);
              return false;
            })
          )
        )
      );
    }

    if ([RoleEnum.Admin, RoleEnum.HR, RoleEnum.Opco].includes(user.Role as RoleEnum)) {
      return this.router.navigate([appRoutesPerRoleDefaults[user.Role]]);
    }

    this.auth.logout();
    return false;
  }
}
